import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STAGES = ["Case performed", "Docs uploaded (72h)", "Eligibility review", "Sorted eligible/ineligible", "Alteva codes & enters", "Accepted by Alteva", "Carrier submission", "Carrier determination", "IDR entry", "IDR entry verified", "IDR determination", "FINAL — paid & reconciled"];

export default function CasePipeline({ active, show }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/masterfile/current`).then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (active === "pipe") load(); }, [active, load]);

  const empty = !data || data.empty;
  const cases = data?.cases || [];
  const flagged = cases.filter((c) => c.exceptions.length || c.aging_flags.length);

  return (
    <section className={`chapter utility${active === "pipe" ? " active" : ""}`} id="pipe">
      <div className="eyebrow">Read-only computed view · source: monthly master case file — no data is entered here</div>
      <h1>Case Pipeline</h1>
      {!empty && <div className="vr-pill open" data-testid="pipe-asof" style={{ marginBottom: 10 }}>DATA AS OF {data.as_of} — monthly master file, up to a month stale by design (oversight layer, not an ops console)</div>}
      <p className="lede">Every case in the latest master file, mapped to the 12-stage lifecycle (Ch. 11 exhibit). The billing gate survives the retired entry form as <b>exception flags</b>: an ineligible case with a handoff date, a handoff with no eligibility status, or a handoff by a lapsed-credential assist surfaces here as a visible exception — the site's job is to catch it, not block it (the handoff already happened in the real world).</p>

      {empty ? (
        <div className="formcard" data-testid="pipe-empty">No data — upload monthly master file. <button className="vr-link" onClick={() => show("mcf")}>Go to Monthly Case File →</button></div>
      ) : (
        <>
          <div className="formcard" data-testid="pipe-stage-board">
            <div className="formcard-title">Cases by stage — {data.row_count} total</div>
            <table className="mform-table"><tbody>
              <tr><th style={{ width: 40 }}>#</th><th>Stage</th><th style={{ width: 70 }}>Cases</th></tr>
              {STAGES.map((s, i) => (
                <tr key={i}><td className="q">{i + 1}</td><td>{s}</td><td data-testid={`pipe-stage-${i + 1}`}>{data.stage_counts[String(i + 1)] || 0}</td></tr>
              ))}
            </tbody></table>
          </div>

          <h2>Exceptions &amp; aging — {flagged.length} case(s) flagged</h2>
          <table data-testid="pipe-exceptions-table"><tbody>
            <tr><th>Case ID</th><th>Stage</th><th>Assist</th><th>Payer</th><th>Flags</th></tr>
            {flagged.length === 0 ? (
              <tr><td colSpan={5}>No exceptions or aging flags in the current file.</td></tr>
            ) : flagged.map((c) => (
              <tr key={c.case_id}>
                <td className="q">{c.case_id}</td><td>{c.current_stage || "—"}</td><td>{c.surgical_assist || "—"}</td><td>{c.payer || "—"}</td>
                <td>{c.exceptions.map((e, i) => <div key={i}><span className="vr-pill declined">{e}</span></div>)}
                    {c.aging_flags.map((f, i) => <div key={i}><span className="vr-pill open">{f}</span></div>)}
                    {c.missed_windows.map((w, i) => <div key={i}><span className="vr-pill declined">MISSED WINDOW: {w}</span></div>)}</td>
              </tr>
            ))}
          </tbody></table>

          <h2>All cases</h2>
          <table data-testid="pipe-cases-table"><tbody>
            <tr><th>Case ID</th><th>DOS</th><th>Stage</th><th>Eligibility</th><th>Payer</th><th>Allowed</th><th>IDR outcome</th><th>Collected</th></tr>
            {cases.map((c) => (
              <tr key={c.case_id}>
                <td className="q">{c.case_id}</td><td>{c.date_of_service}</td><td>{c.current_stage || "—"}</td>
                <td>{c.eligibility_status ? (c.eligibility_status === "Ineligible" ? <span className="vr-pill declined">INELIGIBLE — {c.reason_code || "?"}</span> : c.eligibility_status) : "—"}</td>
                <td>{c.payer || "—"}</td><td>{c.allowed_amount != null ? `$${Number(c.allowed_amount).toLocaleString()}` : "—"}</td>
                <td>{c.idr_outcome || "—"}</td><td>{c.amount_collected != null ? `$${Number(c.amount_collected).toLocaleString()}` : "—"}</td>
              </tr>
            ))}
          </tbody></table>
        </>
      )}
      <p style={{ color: "var(--mute)", fontSize: "13px" }}>Ineligible cases are an operating outcome (~30% typical), reported by controlled reason code — completeness of determination is the compliance standard. Pre-conversion pipeline records live in the legacy archive (binder exports).</p>
    </section>
  );
}
